import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import { Modal, Summary } from './Overlay';
import { Fieldset, Field, FormActions, FormCard } from './Form';

/*
 * Push notifications — layar `push` paket client.
 *
 * Counts, areas and localized content all come from the same GraphQL path used
 * by the sender, so the confirmation cannot drift from the actual recipient set.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($audience: String, $area: String) {
  getBroadcastAudience(audience: $audience, area: $area) {
    devices people customerDevices providerDevices arabicDevices areas pushConfigured status errorMessage
  }
}`;

const M_SEND = `mutation ($audience: String!, $area: String, $idempotencyKey: String!, $title: String!, $message: String!, $titleAr: String, $messageAr: String, $reason: String!) {
  sendBroadcast(audience: $audience, area: $area, idempotencyKey: $idempotencyKey, title: $title, message: $message, titleAr: $titleAr, messageAr: $messageAr, reason: $reason) {
    devices status errorMessage
  }
}`;

const ok = res => res && Number(res.status) === 200;

const AUDIENCES = [
  ['customers', 'Customers only'],
  ['providers', 'Providers only'],
  ['all', 'Everyone — customers and providers']
];

// Dari prototipe: reasonOptions pada sendBroadcast.
const REASONS = ['Service announcement', 'Promotion', 'Incident notice',
  'App update required', 'Other'];

// iOS memotong judul yang panjang di layar kunci.
const TITLE_LIMIT = 40;
const isArabicLocale = locale => String(locale || '').toLowerCase().startsWith('ar');
const localizeError = (message, arabic) => {
  if (!arabic || !message) return message;
  const text = String(message);
  const known = [
    [/valid idempotency key/i, 'مفتاح الطلب غير صالح.'],
    [/already processed/i, 'تمت معالجة هذا الطلب من قبل.'],
    [/every 15 minutes/i, 'لا يُسمح إلا بإرسال إشعار جماعي واحد كل 15 دقيقة.'],
    [/daily broadcast limit \((\d+)\)/i, (_, limit) => `تم الوصول إلى الحد اليومي للإشعارات الجماعية (${limit}).`],
    [/unknown audience/i, 'الجمهور المحدد غير صالح.'],
    [/title and a message are both required/i, 'العنوان والرسالة مطلوبان.'],
    [/40 characters or fewer/i, 'يجب ألا يتجاوز العنوان 40 حرفًا.'],
    [/reason is required/i, 'السبب مطلوب قبل إرسال الإشعار الجماعي.'],
    [/valid reason/i, 'اختر سببًا صالحًا قبل إرسال الإشعار الجماعي.'],
    [/no push notification key/i, 'لم يتم إعداد مفتاح إشعارات الدفع، لذلك لا يمكن الإرسال.'],
    [/no devices match/i, 'لا توجد أجهزة مطابقة لهذا الجمهور.'],
    [/arabic title is required/i, 'العنوان العربي مطلوب للأجهزة العربية.'],
    [/device\(s\) failed/i, 'تعذر قبول الإشعار على بعض الأجهزة.']
  ];
  const match = known.find(([pattern]) => pattern.test(text));
  return match ? (typeof match[1] === 'function' ? match[1](...text.match(match[0])) : match[1])
    : 'تعذر تنفيذ العملية. حاول مرة أخرى.';
};
const newIdempotencyKey = () => (window.crypto && window.crypto.randomUUID
  ? window.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

class PushNotifications extends React.Component {
  state = {
    audience: 'customers', area: '', areas: [], title: '', message: '', titleAr: '', messageAr: '', idempotencyKey: '',
    devices: 0, people: 0, customerDevices: 0, providerDevices: 0, arabicDevices: 0,
    pushConfigured: true, loading: true, error: null,
    confirming: false, reason: '', sending: false, notice: null
  };

  componentDidMount() { this.loadAudience(); }

  componentDidUpdate(prevProps) {
    if (prevProps.currentLocale !== this.props.currentLocale) this.loadAudience();
  }

  loadAudience = async () => {
    this.setState({ loading: true });
    const json = await gql(Q, {
      audience: this.state.audience,
      area: this.state.area || null
    }).catch(() => null);
    const res = json && json.data && json.data.getBroadcastAudience;
    if (!ok(res)) {
      const arabic = isArabicLocale(this.props.currentLocale);
      this.setState({ loading: false, error: localizeError((res && res.errorMessage)
        || (arabic ? 'تعذر حساب الجمهور.' : 'Could not count the audience.'), arabic) });
      return;
    }
    this.setState({
      devices: res.devices, people: res.people,
      customerDevices: res.customerDevices, providerDevices: res.providerDevices,
      arabicDevices: res.arabicDevices, areas: res.areas || [],
      pushConfigured: res.pushConfigured, loading: false, error: null
    });
  };

  setAudience = e => this.setState({ audience: e.target.value }, () => this.loadAudience());

  setArea = e => this.setState({ area: e.target.value }, () => this.loadAudience());

  reset = () => this.setState({ title: '', message: '', titleAr: '', messageAr: '', notice: null });

  send = async () => {
    const { audience, area, title, message, titleAr, messageAr, reason } = this.state;
    const idempotencyKey = this.state.idempotencyKey || newIdempotencyKey();
    this.setState({ idempotencyKey });
    this.setState({ sending: true });
    const json = await gql(M_SEND, {
      audience, area: area || null, idempotencyKey, title: title.trim(), message: message.trim(),
      titleAr: titleAr.trim() || null, messageAr: messageAr.trim() || null, reason
    }).catch(() => null);
    const res = json && json.data && json.data.sendBroadcast;
    this.setState({ sending: false, confirming: false });

    if (!ok(res)) {
      const arabic = isArabicLocale(this.props.currentLocale);
      this.setState({ notice: { tone: 'danger', text: localizeError((res && res.errorMessage)
        || (arabic ? 'تعذر إرسال الإشعار الجماعي.' : 'The broadcast was not sent.'), arabic) } });
      return;
    }
    this.setState({
      title: '', message: '', reason: '', idempotencyKey: '',
      notice: {
        tone: 'info',
        text: isArabicLocale(this.props.currentLocale)
          ? `تم الإرسال إلى ${res.devices.toLocaleString('en-US')} جهازًا. تم تسجيل العملية في سجل التدقيق مع حسابك والسبب.`
          : `Sent to ${res.devices.toLocaleString('en-US')} devices. It is recorded in the audit log with your account and the reason.`
      }
    });
  };

  render() {
    const {
      audience, area, areas, title, message, titleAr, messageAr, devices, people,
      customerDevices, providerDevices, arabicDevices,
      pushConfigured, loading, error, confirming, reason, sending, notice
    } = this.state;
    const isArabic = isArabicLocale(this.props.currentLocale);
    const copy = isArabic ? {
      titlePage: 'الإشعارات الفورية', lead: 'إرسال إشعار جماعي إلى تطبيقات العملاء أو مقدمي الخدمة.',
      communications: 'الاتصالات', classic: 'فتح النسخة الكلاسيكية',
      sendTo: 'إرسال إلى', area: 'تحديد المنطقة', allAreas: 'جميع المناطق',
      customerDevices: 'أجهزة العملاء', providerDevices: 'أجهزة مقدمي الخدمة',
      devicesReached: 'الأجهزة المستلمة', devices: 'أجهزة', message: 'الرسالة',
      title: 'العنوان', titleArabic: 'العنوان (بالعربية)', messageArabic: 'الرسالة (بالعربية)',
      people: 'أشخاص', receive: 'سيتلقون هذه الرسالة', counting: 'جارٍ حساب الأجهزة…',
      optionalArabic: 'اختياري عند عدم تحديد أجهزة عربية', registered: 'مسجلة', dismiss: 'إغلاق',
      cancel: 'إلغاء', saving: 'جارٍ الإرسال…',
      countError: 'تعذر حساب الجمهور.', sendError: 'تعذر إرسال الإشعار الجماعي.',
      sent: devices => `تم الإرسال إلى ${devices} جهازًا. تم تسجيل العملية في سجل التدقيق مع حسابك والسبب.`,
      warning: 'يصل الإشعار الجماعي إلى كل جهاز محدد في الوقت نفسه ولا يمكن التراجع عنه. يظهر عدد الأجهزة قبل الإرسال، ويتم تسجيل كل إرسال في سجل التدقيق مع حسابك والسبب.',
      noKey: 'لم يتم حفظ مفتاح إشعارات الدفع في إعدادات الموقع، لذلك لا يمكن الإرسال حتى تتم إضافته.',
      confirm: count => `إرسال إلى ${count} جهاز؟`, noRecall: 'لا يمكن التراجع عن إشعارات الدفع بعد إرسالها.',
      audience: 'الجمهور', peopleLabel: 'الأشخاص', titleArabicLabel: 'العنوان (بالعربية)',
      fallback: 'استخدام الإنجليزية كبديل', reasonLabel: 'سبب هذا الإرسال الجماعي',
      chooseReason: 'اختر سببًا…', reasonRequired: 'السبب مطلوب وسيتم تسجيله في سجل التدقيق.',
      review: 'مراجعة وإرسال', titlePlaceholder: 'سحب مجاني في نهاية هذا الأسبوع',
      messagePlaceholder: 'احجز خدمة الإنقاذ قبل يوم الأحد وسيتم إلغاء رسوم الاستدعاء.',
      audiences: ['العملاء فقط', 'مقدمو الخدمة فقط', 'الجميع — العملاء ومقدمو الخدمة'],
      reasons: ['إعلان خدمة', 'عرض ترويجي', 'إشعار بحادث', 'مطلوب تحديث التطبيق', 'أخرى']
    } : {
      titlePage: 'Push notifications', lead: 'Send a broadcast to the customer or provider apps.',
      communications: 'Communications', classic: 'Open in classic',
      sendTo: 'Send to', area: 'Limit to area', allAreas: 'All areas',
      customerDevices: 'customer devices', providerDevices: 'provider devices',
      devicesReached: 'Devices reached', devices: 'devices', message: 'Message',
      title: 'Title', titleArabic: 'Title (Arabic)', messageArabic: 'Message (Arabic)',
      people: 'people', receive: 'will receive this', counting: 'Counting devices…',
      optionalArabic: 'Optional when no Arabic devices are selected', registered: 'registered', dismiss: 'Dismiss',
      cancel: 'Cancel', saving: 'Sending…',
      countError: 'Could not count the audience.', sendError: 'The broadcast was not sent.',
      sent: devices => `Sent to ${devices} devices. It is recorded in the audit log with your account and the reason.`,
      warning: 'A broadcast reaches every selected device at once and cannot be recalled. The device count is shown before sending, and every send is recorded in the audit log with your account and a reason.',
      noKey: 'No push notification key is stored in site settings, so nothing can be sent until one is added.',
      confirm: count => `Send to ${count} device${count === 1 ? '' : 's'}?`, noRecall: 'Push notifications cannot be recalled once sent.',
      audience: 'Audience', peopleLabel: 'People', titleArabicLabel: 'Title (Arabic)',
      fallback: 'English fallback', reasonLabel: 'Reason for this broadcast',
      chooseReason: 'Choose a reason…', reasonRequired: 'A reason is required. It is written to the audit log.',
      review: 'Review and send', titlePlaceholder: 'Free tow this weekend',
      messagePlaceholder: 'Book any recovery before Sunday and the callout fee is waived.',
      audiences: ['Customers only', 'Providers only', 'Everyone — customers and providers'],
      reasons: REASONS
    };

    const ready = title.trim() && message.trim() && title.trim().length <= TITLE_LIMIT
      && (!arabicDevices || titleAr.trim()) && devices > 0 && pushConfigured;
    const overLimit = title.trim().length > TITLE_LIMIT;
    const label = copy.audiences[AUDIENCES.findIndex(a => a[0] === audience)] || copy.audiences[0];

    return (
      <AdminShell
        current="push"
        breadcrumb={[copy.communications, copy.titlePage]}
        title={copy.titlePage}
        lead={copy.lead}
        actions={[
          <a className="btn ghost" href="/siteadmin/notifications" key="c">{copy.classic}</a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{copy.dismiss}</button>
          </div>
        )}

        <div className="notice danger">
          <span className="ni">!</span>
          <span className="nt">
            {copy.warning}
          </span>
        </div>

        {!pushConfigured && !loading && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {copy.noKey}
            </span>
          </div>
        )}

        {error && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{error}</span>
          </div>
        )}

        {confirming && (
          <Modal
            icon="bell" tone="danger"
            title={copy.confirm(devices.toLocaleString('en-US'))}
            sub={copy.noRecall}
            confirmLabel={isArabic ? 'إرسال الآن' : 'Send now'}
            cancelLabel={copy.cancel}
            workingLabel={copy.saving}
            busy={sending}
            disabled={!reason}
            onConfirm={this.send}
            onClose={() => this.setState({ confirming: false, reason: '' })}
          >
            <Summary pairs={[
              [copy.audience, label],
              [copy.area, area || copy.allAreas],
              [copy.devicesReached, devices.toLocaleString('en-US')],
              [copy.peopleLabel, people.toLocaleString('en-US')],
              [copy.title, title.trim()],
              [copy.titleArabicLabel, titleAr.trim() || copy.fallback],
              [copy.message, message.trim()]
            ]} />
            <div className="field">
              <label>{copy.reasonLabel}</label>
              <select className="inp" value={reason}
                onChange={e => this.setState({ reason: e.target.value })}>
                <option value="">{copy.chooseReason}</option>
                {REASONS.map((r, i) => <option key={r} value={r}>{copy.reasons[i]}</option>)}
              </select>
              {!reason && <span className="hint">{copy.reasonRequired}</span>}
            </div>
          </Modal>
        )}

        <FormCard
          actions={
            <FormActions
              note={loading
                ? copy.counting
                : isArabic
                  ? `${devices.toLocaleString('en-US')} جهازًا عبر ${people.toLocaleString('en-US')} ${copy.people} ${copy.receive}`
                  : `${devices.toLocaleString('en-US')} device${devices === 1 ? '' : 's'} `
                    + `across ${people.toLocaleString('en-US')} `
                    + `${people === 1 ? 'person' : 'people'} ${copy.receive}`}
              dirty={!!ready}
              busy={sending}
              onSave={() => this.setState({ confirming: true, reason: '', notice: null })}
              onCancel={this.reset}
              saveLabel={copy.review}
              cancelLabel={copy.cancel}
              busyLabel={copy.saving}
            />
          }
        >
          <Fieldset title={copy.audience}>
            <Field label={copy.sendTo}
              hint={`${customerDevices.toLocaleString('en-US')} ${copy.customerDevices}, `
                + `${providerDevices.toLocaleString('en-US')} ${copy.providerDevices} ${copy.registered}.`}>
              <select className="inp" value={audience} onChange={this.setAudience}>
                {AUDIENCES.map(([v], i) => <option key={v} value={v}>{copy.audiences[i]}</option>)}
              </select>
            </Field>
            <Field label={copy.area}>
              <select className="inp" value={area} onChange={this.setArea}>
                <option value="">{copy.allAreas}</option>
                {areas.map(value => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
            <Field label={copy.devicesReached}>
              {/* Dibungkus: .tag di dalam .field menjadi anak flex dan melar
                  selebar kolomnya kalau dibiarkan sendirian. */}
              <span style={{ display: 'flex' }}>
                <span className={`tag ${devices ? 'amber' : 'danger'}`}>
                  <i />{loading ? copy.counting : `${devices.toLocaleString('en-US')} ${copy.devices}`}
                </span>
              </span>
            </Field>
          </Fieldset>

          <Fieldset title={copy.message}
            desc={isArabic
              ? 'تُرسل الرسائل الإنجليزية إلى الأجهزة التي تستخدم الإنجليزية، وتُرسل الرسائل العربية إلى الأجهزة التي تستخدم العربية. يُطلب العنوان العربي عند تحديد أجهزة عربية.'
              : 'English is sent to English devices and Arabic to Arabic devices. Arabic title is required when Arabic devices are selected.'}>
            <Field label={copy.title} required span
              error={overLimit
                ? (isArabic
                  ? `${title.trim().length} حرفًا — قد يقتطع iOS العنوان بعد ${TITLE_LIMIT} حرفًا.`
                  : `${title.trim().length} characters — iOS truncates past about ${TITLE_LIMIT}.`)
                : null}
              hint={overLimit ? null : `${title.trim().length}/${TITLE_LIMIT} ${isArabic ? 'حرفًا' : 'characters'}`}>
              <input className="inp" value={title} placeholder={copy.titlePlaceholder}
                onChange={e => this.setState({ title: e.target.value, notice: null })} />
            </Field>
            <Field label={copy.titleArabic} required={!!arabicDevices} span
              hint={arabicDevices ? `${arabicDevices.toLocaleString('en-US')} ${isArabic ? 'أجهزة عربية' : 'Arabic devices'}` : copy.optionalArabic}>
              <input className="inp" dir="rtl" value={titleAr} placeholder="سحب مجاني هذا الأسبوع"
                onChange={e => this.setState({ titleAr: e.target.value, notice: null })} />
            </Field>
            <Field label={copy.message} required span>
              <textarea className="inp" rows={3} value={message}
                placeholder={copy.messagePlaceholder}
                onChange={e => this.setState({ message: e.target.value, notice: null })} />
            </Field>
            <Field label={copy.messageArabic} span>
              <textarea className="inp" dir="rtl" rows={3} value={messageAr}
                placeholder="احجز خدمة الإنقاذ قبل يوم الأحد"
                onChange={e => this.setState({ messageAr: e.target.value, notice: null })} />
            </Field>
          </Fieldset>
        </FormCard>
      </AdminShell>
    );
  }
}

const mapState = state => ({
  currentLocale: state && state.intl && state.intl.locale
});

export default connect(mapState)(PushNotifications);
